import { BlockPreviewEntry, ContainerPreviewGroup, ExerciseLinkPreviewEntry } from '../../courses/preview/TopicPreviewPanel';
import VideoEmbedPreview from '../../../components/VideoEmbedPreview';

// Renders the visual for ONE canvas node - a pin (referencing an existing
// topic content entry) or a freeform annotation (pencil stroke / shape /
// text / image). Purely presentational - all interaction (drag,
// draw-in-progress) is handled by the parent (TopicCanvasView.jsx), same
// split as conversations-creator-canvas's QuestionNode.jsx.
//
// Pins reuse the admin course-preview's OWN content renderers
// (BlockPreviewEntry/ContainerPreviewGroup/ExerciseLinkPreviewEntry from
// TopicPreviewPanel.jsx) instead of a bespoke truncated-label card - the
// user explicitly asked for pins to show real, readable content "the same
// as in the course", not a summary. Sized by content (no fixed
// width/height in CSS), wrapped in .topic-canvas-pin only for the
// draggable-card chrome (border/background/shadow).
export default function CanvasNodeRenderer({ node, allElements }) {
   if (node.renderKind === 'PIN') {
      if (node.pinKind === 'topicElement') {
         return (
            <div className='topic-canvas-pin'>
               <BlockPreviewEntry block={node.targetItem} />
            </div>
         );
      }
      if (node.pinKind === 'topicContainer') {
         return (
            <div className='topic-canvas-pin topic-canvas-pin-container'>
               <ContainerPreviewGroup container={node.targetItem} elements={allElements || []} />
            </div>
         );
      }
      return (
         <div className='topic-canvas-pin topic-canvas-pin-exercise'>
            <ExerciseLinkPreviewEntry link={node.targetItem} />
         </div>
      );
   }

   if (node.renderKind === 'PENCIL') {
      const points = (node.config && node.config.points) || [];
      if (points.length < 2) return null;
      const minX = Math.min(...points.map((p) => p[0]));
      const minY = Math.min(...points.map((p) => p[1]));
      const d = points.map((p, i) => (i === 0 ? 'M' : 'L') + (p[0] - minX) + ' ' + (p[1] - minY)).join(' ');
      const maxX = Math.max(...points.map((p) => p[0]));
      const maxY = Math.max(...points.map((p) => p[1]));
      return (
         <svg width={maxX - minX + 8} height={maxY - minY + 8} style={{ overflow: 'visible', pointerEvents: 'none' }}>
            <path d={d} fill='none' stroke={node.color} strokeWidth={3} strokeLinecap='round' strokeLinejoin='round' />
         </svg>
      );
   }
   if (node.renderKind === 'RECT') {
      return <div className='topic-canvas-shape-rect' style={{ width: node.width, height: node.height, borderColor: node.color }} />;
   }
   if (node.renderKind === 'ELLIPSE') {
      return <div className='topic-canvas-shape-ellipse' style={{ width: node.width, height: node.height, borderColor: node.color }} />;
   }
   if (node.renderKind === 'ARROW') {
      // dx/dy = end point's offset FROM the start point (node.x/node.y),
      // fixed at draw time and never touched by a translate-drag (only
      // node.x/node.y change during a drag - see useNodeDrag.js). Rendering
      // must treat the start point as LOCAL (0,0) always, never re-derive
      // it from node.x - previously this used an absolute end point
      // (config.x2/y2) together with `node.x - minX` for the start point,
      // which double-counted the live drag delta (once via the wrapper's
      // own left/top, again via minX being a function of the same live
      // node.x) - the arrow didn't translate, it sheared/rotated around
      // the fixed end point as you dragged. Old saved arrows (config.x2/y2,
      // no dx/dy yet) still render correctly once via this fallback, and
      // get migrated to dx/dy the next time they're moved (persistPayload
      // just re-saves whatever's in node.config).
      const dx = (node.config && node.config.dx != null) ? node.config.dx
         : (node.config && node.config.x2 != null) ? node.config.x2 - node.x : (node.width || 100);
      const dy = (node.config && node.config.dy != null) ? node.config.dy
         : (node.config && node.config.y2 != null) ? node.config.y2 - node.y : 0;
      const originX = Math.min(0, dx), originY = Math.min(0, dy);
      const w = Math.max(Math.abs(dx), 1), h = Math.max(Math.abs(dy), 1);
      const x1r = 0 - originX, y1r = 0 - originY, x2r = dx - originX, y2r = dy - originY;
      const angle = Math.atan2(y2r - y1r, x2r - x1r);
      const headLen = 12;
      const hx1 = x2r - headLen * Math.cos(angle - Math.PI / 6), hy1 = y2r - headLen * Math.sin(angle - Math.PI / 6);
      const hx2 = x2r - headLen * Math.cos(angle + Math.PI / 6), hy2 = y2r - headLen * Math.sin(angle + Math.PI / 6);
      return (
         <svg width={w + 8} height={h + 8} style={{ position: 'absolute', left: originX, top: originY, overflow: 'visible', pointerEvents: 'none' }}>
            <line x1={x1r} y1={y1r} x2={x2r} y2={y2r} stroke={node.color} strokeWidth={3} strokeLinecap='round' />
            <path d={`M${hx1} ${hy1} L${x2r} ${y2r} L${hx2} ${hy2}`} fill='none' stroke={node.color} strokeWidth={3} strokeLinecap='round' strokeLinejoin='round' />
         </svg>
      );
   }
   if (node.renderKind === 'TEXT') {
      return (
         <div className='topic-canvas-text-node' style={{ color: node.color, minWidth: node.width || 120 }}>
            {(node.config && node.config.text) || ''}
         </div>
      );
   }
   if (node.renderKind === 'IMAGE') {
      const url = node.config && node.config.fileUrl;
      return url ? (
         <img className='topic-canvas-image-node' src={resolveCrmMediaUrl(url)} alt='' style={{ width: node.width || 200, height: node.height || 'auto' }} />
      ) : null;
   }
   if (node.renderKind === 'VIDEO_EMBED') {
      return <VideoEmbedPreview url={node.config && node.config.url} description={node.config && node.config.description} fill />;
   }
   return null;
}

// A group-shared canvas's IMAGE elements can originate from either side - a
// student's own upload (english-app's canvasApi.js) stores fileUrl as
// /platform-api/files/{id}/content, since that's the only file-serving
// path their auth domain can reach; mirrorCanvasCreate then copies that
// exact string verbatim into every sibling's own CanvasElement row,
// including this one's. CRM's browser session only ever carries the admin
// cookie, so loading a /platform-api/ URL 401s and the <img> renders
// broken - confirmed live (a student-uploaded image never appeared on the
// teacher's group canvas even though the CanvasElement row itself synced
// correctly). The platform side already solves the identical problem in
// reverse for CRM-authored file URLs (resolvePlatformMediaUrl in
// english-app's mediaUrl.ts) - this is that same rewrite, mirrored.
function resolveCrmMediaUrl(url) {
   return url.startsWith('/platform-api/') ? '/api/' + url.slice('/platform-api/'.length) : url;
}
